import React from 'react';
import { Modal, DataCard, Loading } from '@brighterion/br-ui';
import { useTranslation } from 'react-i18next';
import { callAll } from 'Utils';
import { Formik } from 'formik';
import { EventNames, EventTypes, trackEvent } from '@brighterion/insights';
import {
    editListValidationSchema,
    ListNameInput,
    NameInputType,
    DescriptionInput,
    DescriptionInputType
} from '../ListForm';
import LinkedRulesCount from '../LinkedRulesCount';
import styles from './EditListOverviewModal.css';

type Props = {
    isLoading: boolean;
    isOpen: boolean;
    onClose: () => void;
    overview: ListManagementApiList;
    onSave: any;
    error?: any;
    resetError: () => void;
};

const EditListOverviewModal = ({ isLoading, isOpen, onClose, overview, onSave, error, resetError }: Props) => {
    const { t } = useTranslation();
    const { listType, name, description, merchantSpecific, deployedRules, sandboxRules, customListType } = overview;
    const combinedClose = callAll(resetError, onClose);
    const OverViewListType = customListType?.listName ? customListType.listName : t(`listTypes.${listType}`, listType);
    return (
        <Modal
            dataTestId='list-overview-modal'
            isOpen={isOpen}
            closeOnClickOutside={false}
            closeModal={onClose}
            containerClass={styles.modalContainer}
            isSubmitting={isLoading}
        >
            <Modal.Title
                id='list-overview-title'
                title={t('Edit List Overview')}
                onClose={combinedClose}
                isSubmitting={isLoading}
            />
            <div className={styles.dataCardContainer}>
                <DataCard label={t('List Type')} data={OverViewListType} dataTestId='overview-modal-list-type' />
                <div className={styles.separator} />
                <DataCard
                    label={t('Linked Rules')}
                    data={<LinkedRulesCount sandboxRules={sandboxRules} deployedRules={deployedRules} />}
                    dataTestId='overview-modal-linked-rules'
                />
            </div>
            <Formik
                enableReinitialize
                initialValues={{ name, description, listType, merchantSpecific }}
                onSubmit={(values) => {
                    onSave(values);
                    trackEvent({
                        eventType: EventTypes.track,
                        eventName: EventNames.click,
                        payload: {
                            clickIdentifier: 'Lists - Edit List Overview',
                            text: 'Edit List Overview'
                        }
                    });
                }}
                validationSchema={editListValidationSchema(t)}
            >
                {(formikProps) => {
                    const { dirty, isValid } = formikProps;
                    const disableSave = isLoading || !dirty || !isValid;

                    return (
                        <>
                            <div className={styles.formContainer}>
                                <ListNameInput
                                    formikProps={(formikProps as unknown) as NameInputType}
                                    resetError={resetError}
                                    error={error}
                                />
                                <DescriptionInput formikProps={(formikProps as unknown) as DescriptionInputType} />
                            </div>

                            <Modal.Footer
                                id='list-overview-footer'
                                onAction={formikProps.submitForm}
                                disableOnCancel={isLoading}
                                disableOnAction={disableSave}
                                onActionText={isLoading ? <Loading size='sm' fillName='white' /> : t('Save')}
                                onClose={combinedClose}
                                onCloseText={t('Cancel')}
                            >
                                {isLoading && <span className={styles.saving}>{t('savingDots')}</span>}
                            </Modal.Footer>
                        </>
                    );
                }}
            </Formik>
        </Modal>
    );
};

export default React.memo(EditListOverviewModal);
